import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IoCheckmarkDoneOutline } from 'react-icons/io5';
import FileList from './FileList';
import { activeSuggestion, pluralize } from './fileLibrary';

// The server suggests folders for at most this many files per request.
export const SUGGEST_BATCH_MAX = 40;

/**
 * Unfiled: files in no folder, each with the folder Atlas suggests for it.
 * When the view opens and some loaded files have no suggestion yet, it asks
 * for suggestions once (one model request) in the background and reloads.
 * autoSuggest false (an admin viewing someone's library): never asks, since
 * the request is billed to the library's owner as Atlas usage.
 */
export default function UnfiledView({
  client,
  refreshKey,
  listProps,
  suggestions,
  bulkBusy,
  onMoveAllSuggested,
  title,
  subtitle,
  emptyTitle,
  emptyHint,
  autoSuggest = true,
}) {
  const [reloadKey, setReloadKey] = useState(0);
  const [autoState, setAutoState] = useState(null); // { status: 'running', count } | { status: 'error' }
  const decidedRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const handleItems = useCallback(({ items: rows, status }) => {
    if (status !== 'ready') return;
    // Decided on the first load only: a later dismiss (suggestion -> null)
    // or reload never asks again in this view.
    if (decidedRef.current) return;
    decidedRef.current = true;
    // suggestion_due false: Atlas already tried this file recently (or it was
    // dismissed), so asking again would do nothing.
    const missing = rows.filter((row) => row.suggestion === null
      && row.suggestion_due !== false
      && !(row.folders && row.folders.length)).length;
    if (!missing || !autoSuggest || !client.suggestFolders) return;
    setAutoState({ status: 'running', count: Math.min(missing, SUGGEST_BATCH_MAX) });
    Promise.resolve()
      .then(() => client.suggestFolders())
      .then((result) => {
        if (!mountedRef.current) return;
        setAutoState(null);
        if (Number(result?.suggested) !== 0) setReloadKey((k) => k + 1);
      })
      .catch(() => {
        if (mountedRef.current) setAutoState({ status: 'error' });
      });
  }, [autoSuggest, client]);

  // Rendered with the rows as shown, so the count follows every accept / dismiss.
  const renderBar = ({ items }) => {
    const suggested = items.filter((row) => activeSuggestion(row));
    if (!autoState && !suggested.length) return null;
    return (
      <div className="fl-suggest-bar">
        {autoState?.status === 'running' ? (
          <span className="fl-suggest-status" aria-live="polite">
            <span className="fl-spinner fl-spinner-xs" aria-hidden="true" />
            Finding folders for {pluralize(autoState.count, 'file')}…
          </span>
        ) : null}
        {autoState?.status === 'error' ? (
          <span className="fl-suggest-status" aria-live="polite">Folder suggestions aren&apos;t available right now.</span>
        ) : null}
        {suggested.length ? (
          <button
            type="button"
            className="fl-btn fl-btn-sm fl-suggest-all"
            disabled={bulkBusy}
            onClick={() => onMoveAllSuggested(suggested)}
          >
            <IoCheckmarkDoneOutline size={15} aria-hidden="true" />
            Move all suggested ({suggested.length})
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <FileList
      {...listProps}
      client={client}
      params={{ unfiled: true }}
      refreshKey={`${refreshKey}.${reloadKey}`}
      title={title}
      subtitle={subtitle}
      headerExtra={renderBar}
      emptyTitle={emptyTitle}
      emptyHint={emptyHint}
      suggestions={suggestions}
      onItemsChange={handleItems}
    />
  );
}
